import React, { useEffect, useState, useContext, useMemo } from "react";
import classes from "./LocationMaster.module.css";
import { Urls } from "../../../../Urls";
import {
  MastersChangeLog,
  findChangedValues,
  saveMastersApi,
} from "../../Utility";
import Loader from "../../../Loader/Loader";
import { toast } from "react-toastify";
//import AutoComplete from "../FreightMaster/AutoComplete/AutoComplete";
import AutoComplete from "../../../Shared/AutosuggestV2/Autosuggestv2";
import _ from "lodash";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import axios from "../../../../Axios/Axios";
import MultiSelectWithCheckbox from "../../../Shared/MultiSelectCheckbox/MultiSelectCheckbox";
import { sendMailToMasters } from "../Utility/utility";
import { UserApiContext } from "../../../UserDetailsContext/UserDetailsContext";
import { MyCompanyProfileContext } from "../../../../MyCompanyProfileContext";
import { useMixPanelForTracking } from "../../../../utils/TrackMixPanel";
import Select from "react-select";

const SQL_INJECTION_PATTERN =
  /(select\s*\*\s*from)|(sleep\s*\(\s*\d+\s*\))|(insert\s+into\s+\w+\s*\([^)]*\)\s*values\s*\([^)]*\))/i;

// Helper to parse time string to Date object
const parseTimeString = (timeStr) => {
  if (!timeStr) return new Date();
  const [hours, minutes] = timeStr.split(":");
  const date = new Date();
  date.setHours(parseInt(hours, 10));
  date.setMinutes(parseInt(minutes, 10));
  return date;
};

// Helper to convert truck_availability to MultiSelect format
const convertTrucksToSelectFormat = (trucks, truckOptions) => {
  if (!trucks || trucks.length === 0) return [];

  return trucks.map((truck) => {
    const option = truckOptions.find(
      (opt) => String(opt.value) === String(truck.truck_type)
    );
    return (
      option || {
        value: String(truck.truck_type),
        label: String(truck.truck_type),
      }
    );
  });
};

const EditLocationMaster = ({
  locationMaster,
  index,
  truckOptions,
  getTime,
  company_id,
  locationMasterList,
  setLocationMasterList,
  selectedOrg,
  superAdmins,
  emailTemplate,
}) => {
  const { userDetails } = useContext(UserApiContext);
  const { companySettingData } = useContext(MyCompanyProfileContext);
  const { TrackMixPanel } = useMixPanelForTracking(
    userDetails,
    companySettingData
  );

  // Consolidated form state
  const [formData, setFormData] = useState({
    location: null,
    locationName: "",
    address: "",
    type: "",
    pincode: "",
    customerName: "",
    gstNo: "",
    openTime: new Date(),
    closeTime: new Date(),
    selectedTrucks: [],
    haltingTime: "",
    currentFuelRate: "",
    newFuelRate: "",
  });

  const [prevData, setPrevData] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [haltingTimeError, setHaltingTimeError] = useState("");

  // Initialize form data from locationMaster
  useEffect(() => {
    const initialData = _.cloneDeep(locationMaster);
    setPrevData(initialData);

    // Parse location timings
    const openTime = initialData.location_timings?.[0]?.open
      ? parseTimeString(initialData.location_timings[0].open)
      : new Date();

    const closeTime = initialData.location_timings?.[0]?.close
      ? parseTimeString(initialData.location_timings[0].close)
      : new Date();

    // Convert truck availability to select format
    // const selectedTrucks = convertTrucksToSelectFormat(
    //   initialData.truck_availability,
    //   truckOptions
    // );

    const selectedTrucks =
      initialData?.truck_availability?.map((truck) => {
        return {
          label: truck?.truck_type,
          value: truck?.truck_type,
        };
      }) ?? [];

    // console.log({ selectedTrucks, truckOptions }, "selectedTrucks");

    setFormData({
      location: initialData.location || null,
      locationName: initialData.location?.location_name || "",
      address: initialData.addressLine1 || initialData.address || "",
      type: initialData.type || "Destination",
      pincode: initialData.pincode || "",
      customerName: initialData.customer_name || "",
      gstNo: initialData.gst_no || "",
      openTime,
      closeTime,
      selectedTrucks,
      haltingTime: initialData.halting_time
        ? initialData.halting_time / 60
        : "",
      currentFuelRate: initialData.current_rate || "",
      newFuelRate: "",
    });
  }, []);

  // Calculate fuel rate difference and JSON payload
  const fuelRateData = useMemo(() => {
    if (!formData.currentFuelRate || !formData.newFuelRate) {
      return { difference: "", jsonData: null };
    }

    const difference =
      ((formData.newFuelRate - formData.currentFuelRate) /
        formData.currentFuelRate) *
      100;

    const jsonData = {
      origins: [
        {
          origin: String(locationMaster.location_code),
          rate_perc: parseFloat(difference.toFixed(2)),
        },
      ],
    };

    return {
      difference: difference.toFixed(2),
      jsonData,
    };
  }, [
    formData.newFuelRate,
    formData.currentFuelRate,
    locationMaster.location_code,
  ]);

  // Generic handler for form field updates
  const updateField = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  // Halting time validation handler
  const handleHaltingTimeChange = (value) => {
    if (value < 0) {
      setHaltingTimeError("Hours must be greater than 0");
    } else if (value > 2000) {
      setHaltingTimeError("Hours cannot be greater than 2000");
    } else {
      setHaltingTimeError("");
    }
    updateField("haltingTime", value);
  };

  // Validate SQL injection
  const validateNoSqlInjection = () => {
    const fieldsToValidate = [
      { value: formData.address, name: "Address" },
      { value: formData.location?.location_name, name: "Origin" },
      { value: formData.pincode, name: "Pincode" },
      { value: locationMaster.location_code, name: "Location code" },
      { value: formData.type, name: "Location type" },
      { value: formData.gstNo, name: "GST No" },
    ];

    for (const field of fieldsToValidate) {
      if (field.value && SQL_INJECTION_PATTERN.test(field.value)) {
        toast.error(`Invalid ${field.name}`);
        return false;
      }
    }
    return true;
  };

  // Validate halting time
  const validateHaltingTime = () => {
    if (
      formData.haltingTime &&
      (formData.haltingTime === "" || isNaN(formData.haltingTime))
    ) {
      toast.error("Please enter valid Halting time!");
      return false;
    }
    return true;
  };

  // Build the edited location object
  const buildEditedLocation = () => {
    // const selectedTrucks =
    //   formData.selectedTrucks?.length > 0
    //     ? formData.selectedTrucks.map((item) => ({ truck_type: item.value }))
    //     : locationMaster.truck_availability || [];

    const selectedTrucks = Array.isArray(formData.selectedTrucks)
      ? formData.selectedTrucks.map((item) => ({ truck_type: item.value }))
      : [];

    const locationTimings =
      formData.openTime && formData.closeTime
        ? [
          {
            open: getTime(formData.openTime),
            close: getTime(formData.closeTime),
          },
        ]
        : locationMaster.location_timings || [];

    return {
      addressLine1: formData.address || locationMaster.addressLine1,
      addressLine2: "",
      location: formData.location || locationMaster.location,
      pincode: String(formData.pincode ?? ""),
      location_code: String(locationMaster.location_code),
      type: formData.type || locationMaster.type,
      customer_name: formData.customerName,
      gst_no: formData.gstNo,
      location_timings: locationTimings,
      truck_availability: selectedTrucks,
      halting_time: formData.haltingTime
        ? formData.haltingTime * 60
        : undefined,
      current_rate: formData.newFuelRate || undefined,
      is_edited: true,
    };
  };

  // Save handler
  const handleEditSave = async () => {
    if (!validateNoSqlInjection() || !validateHaltingTime()) {
      return;
    }

    setIsLoading(true);

    try {
      const editedLocation = buildEditedLocation();
      const changedValues = findChangedValues(
        prevData,
        editedLocation,
        "changed_value"
      );
      const previousData = findChangedValues(
        prevData,
        changedValues,
        "original_value"
      );

      await saveMastersApi(
        company_id,
        "location",
        [editedLocation],
        selectedOrg,
        TrackMixPanel
      );

      // Send email notification
      await sendMailToMasters(
        "Location Master",
        emailTemplate,
        superAdmins,
        { action: "Location Edited", lane: editedLocation },
        setIsLoading,
        userDetails?.user_name || userDetails?.email?.split("@")[0]
      );

      // Update fuel rate if provided
      if (fuelRateData.jsonData) {
        await axios.post(
          `${Urls.fuel_rate}freight_master?action=update_freight_rates`,
          fuelRateData.jsonData
        );
      }

      // Update local state
      const updatedList = locationMasterList.map((item, idx) =>
        idx === index ? editedLocation : item
      );
      setLocationMasterList(updatedList);

      // Log changes
      const activity = {
        location_code: editedLocation.location_code,
        location_name: editedLocation.addressLine1,
        existing_data: previousData,
        updated_data: changedValues,
      };

      MastersChangeLog(
        "Line Item Updated",
        "location_master",
        userDetails.email,
        userDetails.user_id,
        company_id,
        activity,
        ""
      );

      TrackMixPanel({
        ModuleName: "masters",
        ScreenName: "edit page",
        Definition: "user has clicked on location master save button",
        Data: `location Code: ${editedLocation?.location_code || "none"
          }, changes: ${JSON.stringify(changedValues)}`,
      });

      toast.success("Details Edited Successfully");
      document.getElementById("close_contactus1")?.click();
    } catch (error) {
      console.error("Error saving location:", error);
      toast.error("Failed to save changes");
    } finally {
      setIsLoading(false);
    }
  };

  const dedupedTruckOptions = useMemo(() => {
    const seen = new Set();
    return (truckOptions || [])
      .map((opt) => ({
        ...opt,
        value: String(opt.value), // normalize type too
      }))
      .filter((opt) => {
        if (seen.has(opt.value)) return false;
        seen.add(opt.value);
        return true;
      });
  }, [truckOptions]);

  return (
    <>
      <div className="row m-0">
        <Loader show={isLoading} />
      </div>

      <div className="d-flex flex-wrap">
        <FormField label="Location" width="30%">
          <AutoComplete
            key="Origin_Key"
            value={
              formData.location
                ? {
                  value: formData.location?.location || formData.locationName,
                  label: formData.location?.location_name || formData.locationName,
                }
                : formData.locationName
                  ? {
                    value: formData.locationName,
                    label: formData.locationName,
                  }
                  : null
            }
            placeholder={
              locationMaster?.location?.location_name ||
              locationMaster?.location_name ||
              "select"
            }
            className={classes["originn"]}

            onChange={(val) => updateField("locationName", val)}   // string
            onSelect={(obj) => {
              updateField("location", obj)                         // full object
              updateField("locationName", obj?.location_name || "")
            }}
          />
        </FormField>

        <FormField label="Address" width="30%">
          <input
            type="text"
            onChange={(e) => updateField("address", e.target.value)}
            value={formData.address}
            style={{ height: "37px" }}
          />
        </FormField>

        <FormField label="Location Type" width="30%">
          <select
            className="form-control"
            style={{ height: "37px" }}
            value={formData.type}
            onChange={(e) => updateField("type", e.target.value)}
          >
            <option value="Destination">Destination</option>
            <option value="Plant">Plant</option>
            <option value="Warehouse">Warehouse</option>
          </select>
        </FormField>

        <FormField label="Location Code" width="30%">
          <input
            type="text"
            disabled
            value={locationMaster.location_code}
            style={{ height: "37px", cursor: "no-drop" }}
          />
        </FormField>

        <FormField label="Pincode" width="30%">
          <input
            type="number"
            onChange={(e) => updateField("pincode", e.target.value)}
            value={formData.pincode}
            style={{ height: "37px" }}
          />
        </FormField>

        <FormField label="Customer Name" width="30%">
          <input
            type="text"
            onChange={(e) => updateField("customerName", e.target.value)}
            value={formData.customerName}
            style={{ height: "37px" }}
          />
        </FormField>

        <FormField label="GST No" width="30%">
          <input
            type="text"
            onChange={(e) => updateField("gstNo", e.target.value)}
            value={formData.gstNo}
            style={{ height: "37px" }}
          />
        </FormField>

        <FormField label="Open Time" width="30%">
          <DatePicker
            selected={formData.openTime}
            onChange={(date) => updateField("openTime", date)}
            showTimeSelect
            showTimeSelectOnly
            timeIntervals={60}
            dateFormat="h:mm aa"
          />
        </FormField>

        <FormField label="Close Time" width="30%">
          <DatePicker
            selected={formData.closeTime}
            onChange={(date) => updateField("closeTime", date)}
            showTimeSelect
            showTimeSelectOnly
            timeIntervals={60}
            dateFormat="h:mm aa"
          />
        </FormField>

        {/* <FormField label="Trucks Available" width="30%">
          <MultiSelectWithCheckbox
            options={truckOptions}
            value={formData.selectedTrucks}
            onChange={(e) => updateField("selectedTrucks", e)}
          />
        </FormField> */}

        <FormField label="Trucks Available" width="30%">
          <Select
            isMulti
            options={dedupedTruckOptions}
            value={formData.selectedTrucks}
            onChange={(selected) =>
              updateField("selectedTrucks", selected || [])
            }
            closeMenuOnSelect={false}
            placeholder="Select trucks..."
            classNamePrefix="react-select"
            styles={{
              container: (base) => ({ ...base, width: "100%" }),
              control: (base) => ({ ...base, minHeight: "37px" }),
            }}
          />
        </FormField>

        <div className="position-relative p-2" style={{ width: "30%" }}>
          <span style={{ fontWeight: "600" }}>Halting Time</span>
          <div style={{ position: "relative" }}>
            <input
              style={{ height: "37px", width: "100%" }}
              type="number"
              value={formData.haltingTime}
              onChange={(e) => handleHaltingTimeChange(e.target.value)}
            />
            <span
              style={{
                position: "absolute",
                right: "4px",
                top: "50%",
                transform: "translateY(-50%)",
                padding: "0.7rem",
              }}
              className="bg-light border-left"
            >
              Minutes
            </span>
          </div>
          {haltingTimeError && (
            <div className="text-danger" style={{ fontSize: "12px" }}>
              {haltingTimeError}
            </div>
          )}
        </div>

        <div className="p-2" style={{ width: "30%" }} />

        <FormField label="Current Fuel Rate" width="30%">
          <input
            type="text"
            disabled
            value={formData.currentFuelRate}
            placeholder={formData.currentFuelRate || "N/A"}
            style={{ height: "37px", cursor: "no-drop" }}
          />
        </FormField>

        <FormField label="New Fuel Rate" width="30%">
          <input
            type="text"
            style={{ height: "37px" }}
            onChange={(e) => {
              const value = parseFloat(e.target.value);
              updateField("newFuelRate", isNaN(value) ? "" : value);
            }}
            value={formData.newFuelRate}
          />
        </FormField>

        <FormField label="% Difference in Fuel Rate" width="30%">
          <input
            type="text"
            value={fuelRateData.difference}
            disabled
            style={{ height: "37px", cursor: "no-drop" }}
            placeholder={fuelRateData.difference || "N/A"}
          />
        </FormField>
      </div>

      <div className="text-center mt-3">
        <button
          className={`${classes["save-btn"]} btn btn-success`}
          onClick={handleEditSave}
          disabled={isLoading}
        >
          Save
        </button>
      </div>
    </>
  );
};

// Helper component for consistent form field layout
const FormField = ({ label, width, children }) => (
  <div className="p-2" style={{ width }}>
    <span style={{ fontWeight: "600" }}>{label}</span>
    <label style={{ width: "100%" }}>{children}</label>
  </div>
);

export default EditLocationMaster;
